import React, {useState} from 'react';
import {Button, IconButton, InputLabel, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {finalize} from 'rxjs';
import {useSnackbar} from 'notistack';

import {executionLineService, ExecutionLinesGroupedAction, ExecutionLineStatus} from '@store/invoices/executionLines';

import ModalComponent from '@components/modal/Modal.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';

import {AddQuantity, Remove} from '@icons/index';

import dayjs from '@utils/dayjs.utils';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import SelectComponent from '@components/inputs/select/Select.component';
import {Sizes} from '@constants/sizes.constants';

interface ExecutionLineGroupedActionModalProps {
  handleClose: () => void;
}

const ExecutionLineGroupedActionModal = (props: ExecutionLineGroupedActionModalProps) => {
  const {handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ExecutionLinesGroupedAction>({});

  const updateData = (newData: ExecutionLinesGroupedAction) => setData((state) => ({...state, ...newData}));

  const handleSave = () => {
    setLoading(true);
    executionLineService.executionLinesGroupedAction(data).pipe(finalize(() => setLoading(false))).subscribe({
      next: (ids) => {
        enqueueSnackbar(t('operations.executions.success.update', {count: ids.length}), {variant: 'success'});
        handleClose();
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

    const handleDelete = () => {
        setLoading(true);
        executionLineService.deleteGroupedExecutionLines().pipe(finalize(() => setLoading(false))).subscribe({
            next: (ids) => {
                enqueueSnackbar(t('operations.executions.success.delete', {count: ids.length}), {variant: 'success'});
                handleClose();
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
        });
    };

  const errors = {
    executedAt: getFieldError(data.executedAt, FieldValidationType.DATE),
  };

  const disabled = !data.executedAt && data.quantity1 === undefined
    && data.unitCost === undefined && data.unitPrice === undefined && !data.status
    && (data.supplierInvoicingDelayed === undefined || (data.supplierInvoicingDelayed as any) === '')
    && (data.clientInvoicingDelayed === undefined || (data.clientInvoicingDelayed as any) === '')
   && data.quantity2 === undefined;

  return (
    <ModalComponent
      title={t('global.groupedAction')}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <DatePickerComponent
            color="secondary"
            labelWidth={Sizes.datePickerWidth}
            label={t('operations.executions.manage.executedAt')}
            placeholder={t('global.selectDate')}
            handleChange={(executedAt) => updateData({executedAt: executedAt ? dayjs(executedAt).toDate() : undefined})}
            value={data.executedAt}
            error={errors.executedAt}/>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: 160}}>
              {t('operations.executions.manage.containerQuantity')}
            </InputLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                disabled={(data.quantity1 || 0) <= 1}
                onClick={() => updateData({quantity1: (data.quantity1 || 0) - 1})}>
                <Remove/>
              </IconButton>
              <NumberTextFieldComponent
                inputWidth={80}
                centered
                pattern={new RegExp(/^[1-9]+\d*$/)}
                handleChange={(value) => {
                  updateData({quantity1: value});
                }}
                value={data.quantity1}/>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                onClick={() => updateData({quantity1: (data.quantity1 || 0) + 1})}>
                <AddQuantity/>
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: 160}}>
              {t('operations.executions.manage.wasteQuantity')}
            </InputLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                disabled={(data.quantity2 || 0) <= 1}
                onClick={() => updateData({quantity2: (data.quantity2 || 0) - 1})}>
                <Remove/>
              </IconButton>
              <NumberTextFieldComponent
                inputWidth={80}
                centered
                pattern={new RegExp(/^[1-9]+\d*$/)}
                handleChange={(value) => {
                  updateData({quantity2: value});
                }}
                value={data.quantity2}/>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                onClick={() => updateData({quantity2: (data.quantity2 || 0) + 1})}>
                <AddQuantity/>
              </IconButton>
            </Stack>
          </Stack>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('suppliers.details.catalog.manage.puBuy')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
            handleChange={(unitCost) => updateData(({unitCost, unitCostInCents: (unitCost || 0) * 100}))}
            value={data.unitCost ?? undefined}/>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('clients.details.contractedServices.manage.puSell')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
            handleChange={(unitPrice) => updateData(({unitPrice, unitPriceInCents: (unitPrice || 0) * 100}))}
            value={data.unitPrice ?? undefined}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('operations.executions.manage.status')}
            handleChange={(status) => updateData({status})}
            value={data.status}
            placeholder={t('global.selectOption')}
            items={ExecutionLineStatus.selectItems()}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('invoices.executions.supplierInvoicingDelayed')}
            handleChange={(supplierInvoicingDelayed) => updateData({supplierInvoicingDelayed})}
            value={data.supplierInvoicingDelayed}
            placeholder={t('global.selectOption')}
            items={[
              {label: t('global.yes'), value: true},
              {label: t('global.no'), value: false},
            ]}/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('invoices.executions.clientInvoicingDelayed')}
            handleChange={(clientInvoicingDelayed) => updateData({clientInvoicingDelayed})}
            value={data.clientInvoicingDelayed}
            placeholder={t('global.selectOption')}
            items={[
              {label: t('global.yes'), value: true},
              {label: t('global.no'), value: false},
            ]}/>
        </Stack>
      )}
    actions={(
        <>
            <Button variant="contained" disabled={loading} onClick={handleDelete} color="error">
                {t('global.delete')}
            </Button>
            <Button
                variant="contained"
                disabled={loading || checkIfErrors(errors) || disabled}
                onClick={handleSave}
                color="primary">
          {t('global.save')}
        </Button>
        </>
      )}/>
  );
};

export default ExecutionLineGroupedActionModal;
